/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 今日龙虾 CLAW（参考 myclaw.ai 深色主题设计）
 */

import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Button, message } from 'antd'
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  BarChartOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CrownOutlined,
  CustomerServiceOutlined,
  DownOutlined,
  FileTextOutlined,
  FireOutlined,
  MessageOutlined,
  RightOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  StarFilled,
  StarOutlined,
  ThunderboltOutlined,
  ToolOutlined
} from '@ant-design/icons'
import { useAppSelector } from '../../app/hooks'
import './index.scss'
import clawIcon from './assets/claw_icon.png'
import smailClawIcon from './assets/smail_claw.png'
import midClawIcon from './assets/mid_claw.png'
import todaycamLogo from '../../images/todaycam_logo.png'
import ClawPaymentModal from './components/ClawPaymentModal'
import hejingQrCode from '../../images/service-qrcode/hejing_qr_image.png'
import wanganqiangQrCode from '../../images/service-qrcode/wanganqiang_qr_image.png'
import wanganqiangAvatar from '../../images/service-qrcode/wanganqiang.png'
import sensors, { sensorsRole, sensorsVipLevel } from '../../utils/sensors'
import { buildClawUrl } from '../../utils/clawUrl'
import {
  CLAW_CONFIG_KEY,
  getPlanSwitchMap,
  isClawActivated,
  isClawBlacklisted,
  getClawTeamAccess,
  PLAN_TYPE_MAP,
  ClawPlanType
} from './clawAccessConfig'

// 动态图标查表（tag.icon / benefit.icon 字符串 → antd6 组件）
const CLAW_ICONS: Record<string, React.ComponentType<any>> = {
  star: StarOutlined,
  'clock-circle': ClockCircleOutlined,
  tool: ToolOutlined,
  'customer-service': CustomerServiceOutlined,
  rocket: RocketOutlined,
  'safety-certificate': SafetyCertificateOutlined
}
const ClawIcon = ({ name }: { name: string }) => {
  const C = CLAW_ICONS[name]
  return C ? <C /> : null
}

type PlanKind = 'starter' | 'standard' | 'flagship'

interface PlanFeature {
  text: string
  highlight?: boolean
}

interface PlanTag {
  label: string
  icon?: string
}

interface PlanItem {
  key: PlanKind
  name: string
  price: number
  features: PlanFeature[]
  buttonText: string
  highlighted?: boolean
  dark?: boolean
  tags?: PlanTag[]
  tagline?: string
  badgeText?: string
}

const PLANS: PlanItem[] = [
  // {
  //   key: 'starter',
  //   name: '小虾 Claw 入门版',
  //   price: 99,
  //   tagline: '花最少的钱先上车，内测期全功能免费体验',
  //   features: [
  //     { text: '智能照片管理' },
  //     { text: '智能数据报告' },
  //     { text: '智能团队管理' },
  //     { text: '智能图片分析' }
  //   ],
  //   buttonText: '立即激活',
  //   tags: [{ label: '小虾·尊享服务群', icon: 'star' }]
  // },
  {
    key: 'standard',
    name: '中虾 Claw 标准版',
    price: 399,
    tagline: '绝大多数团队的选择，激活后内测期间不限功能免费用',
    features: [
      { text: '智能照片管理' },
      { text: '智能数据报告' },
      { text: '智能团队管理' },
      { text: '智能图片分析' },
      { text: '智能数据看板', highlight: true },
      { text: '10个高阶Skill', highlight: true }
    ],
    buttonText: '立即激活',
    highlighted: true,
    badgeText: '最受欢迎',
    tags: [{ label: '中虾·尊享服务群', icon: 'star' }]
  },
  {
    key: 'flagship',
    name: '大虾 Claw 旗舰版',
    price: 999,
    tagline: '优先排期、专属对接、定制场景，VIP级深度共建机会',
    features: [
      { text: '智能照片管理' },
      { text: '智能数据报告' },
      { text: '智能团队管理' },
      { text: '智能图片分析' },
      { text: '智能数据看板' },
      { text: '30个高阶Skill', highlight: true },
      { text: '专属产品顾问', highlight: true }
    ],
    buttonText: '立即激活',
    dark: true,
    badgeText: '尊享服务',
    tags: [
      { label: '大虾·尊享服务群', icon: 'star' },
      { label: '7×24 产品专员尊享一对一服务', icon: 'clock-circle' }
    ]
  }
]

const HOW_TO_START = [
  {
    step: 1,
    icon: 'check-square',
    title: '选择套餐',
    subtitle: '3 种规格任选，每个套餐都是独立专属实例'
  },
  {
    step: 2,
    icon: 'cloud',
    title: '极速部署',
    subtitle: '我们为你单独部署优化，2-3 个工作日即可上线'
  },
  {
    step: 3,
    icon: 'desktop',
    title: '开箱即用',
    subtitle: '登录就能用，无需配置，7×24 小时随时待命'
  }
]

const BENEFITS = [
  {
    icon: 'clock-circle',
    title: '每天节省 2 小时，专注核心业务',
    subtitle:
      '告别繁琐的考勤统计，AI 自动处理打卡数据、生成报表，让你有更多时间做重要的事'
  },
  {
    icon: 'check-circle',
    title: '零失误，考勤数据准确可靠',
    subtitle:
      '95% 以上识别准确率，自动发现异常打卡，再也不用担心漏统计、算错工时'
  },
  {
    icon: 'smile',
    title: '团队都说好用，老板更满意',
    subtitle:
      '员工打卡更方便，管理者随时查看数据，决策层获得深度分析，人人都省心'
  }
]

const STATS = [
  { value: '10万+', label: '每日处理考勤记录' },
  { value: '80%', label: '节省人工统计时间' },
  { value: '50+', label: '支持工作场景' },
  { value: '95%', label: 'AI识别准确率' }
]

const COMPARISON = [
  {
    feature: '考勤统计效率',
    normal: '人工统计，每天耗时 2-3 小时',
    claw: 'AI 自动处理，3 分钟完成',
    highlight: true
  },
  {
    feature: '数据准确率',
    normal: '人工易出错，准确率约 85%',
    claw: 'AI 识别，准确率 95% 以上',
    highlight: true
  },
  {
    feature: '异常处理',
    normal: '需人工逐一核对，容易遗漏',
    claw: '自动识别异常并发送提醒'
  },
  {
    feature: '报表生成',
    normal: '手动制作，格式不统一',
    claw: '一键生成，自动发送相关人员'
  },
  {
    feature: '人力成本',
    normal: '需专人负责，月成本 5000+',
    claw: '全自动化，节省 80% 人力'
  },
  {
    feature: '数据分析',
    normal: '只有基础统计，无深度洞察',
    claw: '智能分析趋势，提供改进建议'
  }
]

// SVG 龙虾图标组件
const LobsterIcon: React.FC<{ type: PlanKind }> = ({ type }) => {
  if (type === 'starter') {
    // 小虾 - 入门版（使用本地图片）
    return (
      <img
        src={smailClawIcon}
        alt="小虾"
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    )
  } else if (type === 'standard') {
    // 中虾 - 标准版（使用本地图片）
    return (
      <img
        src={midClawIcon}
        alt="中虾"
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    )
  } else {
    // 大虾 - 旗舰版（使用本地图片）
    return (
      <img
        src={clawIcon}
        alt="大虾"
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    )
  }
}

const BETA_DEADLINE = new Date('2026-04-06T23:59:59+08:00').getTime()

const TodayClaw: React.FC = () => {
  const [visibleMessages, setVisibleMessages] = useState<number>(0)
  const [isTyping, setIsTyping] = useState<boolean>(false)
  const [showBackButton, setShowBackButton] = useState<boolean>(false)
  const [isKaChannel, setIsKaChannel] = useState<boolean>(false)
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<PlanItem | null>(null)
  const [userMessages, setUserMessages] = useState<
    { type: 'user' | 'bot'; text: string }[]
  >([])
  const [inputValue, setInputValue] = useState('')
  const [isBotReplying, setIsBotReplying] = useState(false)
  const [showServiceQr, setShowServiceQr] = useState(false)
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  })
  const chatRef = useRef<HTMLDivElement>(null)
  const pricingRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const group = useAppSelector((state: any) => state.group)
  const groupID = group?.groupId || ''
  const teamShortId = String(group?.invite_id || group?.groupId || '')
  const myRole: number = group?.myRole ?? 0

  // ---- 从远程配置读取 ----
  const mainConfig = useAppSelector((state: any) => state.main?.config)
  const remoteConfigValue: string | undefined = mainConfig?.[CLAW_CONFIG_KEY]
  const planSwitchMap = useMemo(
    () => getPlanSwitchMap(remoteConfigValue),
    [remoteConfigValue]
  )

  // ---- 黑名单检查：大B端客户屏蔽整个预售页 ----
  const blacklisted = useMemo(
    () =>
      isClawBlacklisted(String(groupID || ''), teamShortId, remoteConfigValue),
    [groupID, teamShortId, remoteConfigValue]
  )

  // ---- 判断当前团队是否已开通内测 ----
  const activated = useMemo(
    () => isClawActivated(teamShortId, myRole, remoteConfigValue),
    [teamShortId, myRole, remoteConfigValue]
  )
  const teamAccess = useMemo(
    () => getClawTeamAccess(teamShortId, remoteConfigValue),
    [teamShortId, remoteConfigValue]
  )

  // ---- 埋点 ----
  const trackClawPage = (
    actionType:
      | 'enterPage'
      | 'back'
      | 'activate'
      | 'viewDetails'
      | 'buySmall'
      | 'buyMedium'
      | 'buyLarge'
      | 'smallPayPop'
      | 'middlePayPop'
      | 'largePayPop'
      | 'contactUs'
  ) => {
    sensors.track('workgroup_b8054_c8055_exposure', {
      requirement_id: 1007,
      track_sign:
        'aworkgroup.b8054.c8055.exposure.workgroup_b8054_c8055_exposure',
      actionType,
      role: sensorsRole(myRole),
      groupID: String(groupID || ''),
      groupVipLevel: sensorsVipLevel(group)
    })
  }

  // 进入页面埋点（仅触发一次）
  const hasTrackedEnterPage = useRef(false)
  useEffect(() => {
    if (hasTrackedEnterPage.current || !groupID) return
    hasTrackedEnterPage.current = true
    trackClawPage('enterPage')
  }, [groupID])

  /** planKey → planType 映射 */
  const PLAN_KEY_TO_TYPE: Record<PlanKind, ClawPlanType> = {
    starter: 1,
    standard: 2,
    flagship: 3
  }

  // 定义所有消息
  const totalMessages = 11

  // 检查是否显示返回按钮
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const fromPlace = params.get('fromPlace')
    setShowBackButton(fromPlace === 'work')
    setIsKaChannel(params.get('channel') === 'ka')
  }, [])

  // 倒计时
  useEffect(() => {
    const tick = () => {
      const now = Date.now()
      const diff = Math.max(0, BETA_DEADLINE - now)
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60)
      })
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  // 自动播放消息动画
  useEffect(() => {
    if (visibleMessages >= totalMessages) return

    const timer = setTimeout(
      () => {
        // 显示"正在输入"动画
        if (visibleMessages % 2 === 0 && visibleMessages < totalMessages) {
          setIsTyping(true)
          setTimeout(() => {
            setIsTyping(false)
            setVisibleMessages(visibleMessages + 1)
            // 滚动到底部
            if (chatRef.current) {
              chatRef.current.scrollTop = chatRef.current.scrollHeight
            }
          }, 800) // 输入动画持续 800ms
        } else {
          setVisibleMessages(visibleMessages + 1)
          // 滚动到底部
          if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight
          }
        }
      },
      visibleMessages === 0 ? 500 : 1200
    ) // 第一条消息 500ms 后出现，后续消息间隔 1200ms

    return () => clearTimeout(timer)
  }, [visibleMessages])

  const handlePurchase = (plan: PlanItem) => {
    // 点击购买埋点
    if (plan.key === 'starter') {
      trackClawPage('buySmall')
    } else if (plan.key === 'standard') {
      trackClawPage('buyMedium')
    } else if (plan.key === 'flagship') {
      trackClawPage('buyLarge')
    }
    setSelectedPlan(plan)
    setPaymentModalVisible(true)
    // 付费弹窗出现埋点
    setTimeout(() => {
      if (plan.key === 'starter') {
        trackClawPage('smallPayPop')
      } else if (plan.key === 'standard') {
        trackClawPage('middlePayPop')
      } else if (plan.key === 'flagship') {
        trackClawPage('largePayPop')
      }
    }, 100)
  }

  const scrollToPricing = () => {
    trackClawPage('viewDetails')
    if (pricingRef.current) {
      pricingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const handleBack = () => {
    trackClawPage('back')
    window.location.href = '/photo/all'
  }

  const handleSendMessage = () => {
    const text = inputValue.trim()
    if (!text || isBotReplying) return

    if (activated) {
      // 已开通 → 跳转 Claw 工作台（携带套餐参数）
      const plan = teamAccess?.plan || 1
      const planName = PLAN_TYPE_MAP[plan as ClawPlanType]?.name || ''
      const clawUrl = buildClawUrl({
        groupId: String(groupID || ''),
        plan,
        planName
      })
      window.open(clawUrl, '_blank', 'noopener,noreferrer')
      return
    }

    // 未开通 → 提示开通
    setUserMessages((prev) => [...prev, { type: 'user', text }])
    setInputValue('')
    setIsBotReplying(true)
    setTimeout(() => {
      if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight
      }
    }, 50)
    setTimeout(() => {
      setUserMessages((prev) => [
        ...prev,
        {
          type: 'bot',
          text: '您尚未开通内测权限，开通后即可体验今日龙虾 Claw 的全部功能。请选择下方套餐参与内测，锁定专属优惠！'
        }
      ])
      setIsBotReplying(false)
      setTimeout(() => {
        if (chatRef.current) {
          chatRef.current.scrollTop = chatRef.current.scrollHeight
        }
      }, 50)
    }, 1000)
  }

  // 黑名单团队显示空白页面
  if (blacklisted) {
    return (
      <div className="claw-page" style={{ minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', paddingTop: 200, color: '#666' }}>
          暂未开放
        </div>
      </div>
    )
  }

  return (
    <div className="claw-page">
      {/* Back Button */}
      {showBackButton && (
        <button
          className="claw-back-button"
          onClick={handleBack}
          aria-label="返回"
        >
          <ArrowLeftOutlined />
          <span>返回</span>
        </button>
      )}

      {/* Hero Section */}
      <section className="claw-hero">
        <div className="claw-hero__container">
          <div className="claw-hero__badge">
            <img
              src={todaycamLogo}
              alt=""
              className="claw-hero__badge-logo"
              aria-hidden
            />
            <span>今日水印相机官方出品</span>
          </div>

          <div className="claw-hero__title-wrap">
            <div className="claw-hero__title-row">
              <img src={clawIcon} alt="Claw" className="claw-hero__icon" />
              <h1 className="claw-hero__title">今日龙虾Claw</h1>
            </div>
            {activated ? (
              <h2 className="claw-hero__line2 claw-hero__line2--active">
                内测已开通
              </h2>
            ) : (
              <h2 className="claw-hero__line2">你的AI数字员工 准备就绪</h2>
            )}
          </div>

          {activated ? (
            <p className="claw-hero__desc">
              尊享
              <span className="claw-hero__accent-plan">
                {teamAccess
                  ? ` ${
                      PLAN_TYPE_MAP[teamAccess.plan]?.name || '今日龙虾 Claw'
                    } `
                  : ' 今日龙虾 Claw '}
              </span>
              内测资格 · 你的专属数字员工已上线
            </p>
          ) : (
            <p className="claw-hero__desc">
              智能照片管理 · 自动考勤统计 · 安全隐患识别 · 数据报表生成
            </p>
          )}

          {/* Stats Row */}
          <div className="claw-hero__stats-row">
            <div className="claw-hero__stat-item">
              <span className="claw-hero__stat-val claw-hero__stat-val--gold">
                2小时+/天
              </span>
              <span className="claw-hero__stat-lbl">节省人工</span>
            </div>
            <span className="claw-hero__stat-sep" />
            <div className="claw-hero__stat-item">
              <span className="claw-hero__stat-val claw-hero__stat-val--green">
                99%+
              </span>
              <span className="claw-hero__stat-lbl">匹配准确率</span>
            </div>
            <span className="claw-hero__stat-sep" />
            <div className="claw-hero__stat-item">
              <span className="claw-hero__stat-val claw-hero__stat-val--blue">
                零人力
              </span>
              <span className="claw-hero__stat-lbl">无需额外招人</span>
            </div>
          </div>

          {/* CTA Row */}
          <div className="claw-hero__cta">
            {activated ? (
              <Button
                size="large"
                className="claw-hero__button claw-hero__button--active"
                onClick={() => {
                  const plan = teamAccess?.plan || 1
                  const planName =
                    PLAN_TYPE_MAP[plan as ClawPlanType]?.name || ''
                  window.open(
                    buildClawUrl({
                      groupId: String(groupID || ''),
                      plan,
                      planName
                    }),
                    '_blank',
                    'noopener,noreferrer'
                  )
                }}
              >
                进入工作台
                <ArrowRightOutlined />
              </Button>
            ) : (
              <>
                <Button
                  size="large"
                  className="claw-hero__button claw-hero__button--primary"
                  onClick={() => {
                    trackClawPage('activate')
                    scrollToPricing()
                  }}
                >
                  激活数字员工
                  <ArrowRightOutlined />
                </Button>
                {!isKaChannel && (
                  <Button
                    size="large"
                    className="claw-hero__button claw-hero__button--ghost"
                    onClick={scrollToPricing}
                  >
                    了解定制服务
                    <DownOutlined />
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Guarantee Bar */}
          <div className="claw-hero__guarantee">
            <span className="claw-hero__guarantee-item">✅ 内测全功能免费</span>
            <span className="claw-hero__guarantee-sep" />
            <span className="claw-hero__guarantee-item">✅ 正式版全额抵扣</span>
            <span className="claw-hero__guarantee-sep" />
            <span className="claw-hero__guarantee-item">✅ 不满意随时退</span>
            <span className="claw-hero__guarantee-sep" />
            <span className="claw-hero__guarantee-item">
              ✅ 专属内测反馈服务
            </span>
          </div>
        </div>

        {/* Background decoration */}
        <div className="claw-hero__bg">
          <div className="claw-hero__glow claw-hero__glow--1"></div>
          <div className="claw-hero__glow claw-hero__glow--2"></div>
        </div>
      </section>

      {/* Main Content */}
      <main className="claw-main">
        {/* Demo Dashboard */}
        <section className="claw-demo">
          <div className="claw-demo__container">
            {/* Glow Effect */}
            <div className="claw-demo__glow"></div>

            <div className="claw-demo__window">
              {/* Window Header */}
              <div className="claw-demo__header">
                <div className="claw-demo__dots">
                  <span className="claw-demo__dot claw-demo__dot--red"></span>
                  <span className="claw-demo__dot claw-demo__dot--yellow"></span>
                  <span className="claw-demo__dot claw-demo__dot--green"></span>
                </div>
                <span className="claw-demo__title">今日龙虾Claw·数字员工</span>
                <div className="claw-demo__status">
                  <span className="claw-demo__status-dot"></span>
                  <span className="claw-demo__status-text">在线</span>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="claw-demo__body">
                {/* Sidebar */}
                <div className="claw-demo__sidebar">
                  <div className="claw-demo__nav">
                    <div className="claw-demo__nav-item claw-demo__nav-item--active">
                      <MessageOutlined className="claw-demo__nav-icon" />
                      <span className="claw-demo__nav-label">对话</span>
                    </div>
                    <div className="claw-demo__nav-item">
                      <CheckSquareOutlined className="claw-demo__nav-icon" />
                      <span className="claw-demo__nav-label">任务</span>
                    </div>
                    <div className="claw-demo__nav-item">
                      <BarChartOutlined className="claw-demo__nav-icon" />
                      <span className="claw-demo__nav-label">数据</span>
                    </div>
                    <div className="claw-demo__nav-item">
                      <SettingOutlined className="claw-demo__nav-icon" />
                      <span className="claw-demo__nav-label">设置</span>
                    </div>
                  </div>

                  {/* User Info */}
                  <div className="claw-demo__user">
                    <div className="claw-demo__user-avatar">张</div>
                    <span className="claw-demo__user-name">张工</span>
                  </div>
                </div>

                {/* Chat Area */}
                <div className="claw-demo__main">
                  <div className="claw-demo__chat" ref={chatRef}>
                    {/* Message 1 - Bot */}
                    {visibleMessages >= 1 && (
                      <div className="claw-demo__message claw-demo__message--bot claw-demo__message--fade-in">
                        <div className="claw-demo__avatar">
                          <img src={clawIcon} alt="Claw" />
                        </div>
                        <div className="claw-demo__bubble claw-demo__bubble--bot">
                          早上好！今天工地已有 23 人完成打卡，发现 5
                          人未按时到岗。需要我生成今日考勤报表吗？
                        </div>
                      </div>
                    )}

                    {/* Message 2 - User */}
                    {visibleMessages >= 2 && (
                      <div className="claw-demo__message claw-demo__message--user claw-demo__message--fade-in">
                        <div className="claw-demo__bubble claw-demo__bubble--user">
                          生成报表，并分析本周考勤异常情况
                        </div>
                      </div>
                    )}

                    {/* Message 3 - Bot with Data */}
                    {visibleMessages >= 3 && (
                      <div className="claw-demo__message claw-demo__message--bot claw-demo__message--fade-in">
                        <div className="claw-demo__avatar">
                          <img src={clawIcon} alt="Claw" />
                        </div>
                        <div className="claw-demo__bubble claw-demo__bubble--bot">
                          <p>本周考勤数据分析完成：</p>
                          <div className="claw-demo__stats-grid">
                            <div className="claw-demo__stat-item">
                              <div className="claw-demo__stat-value">156</div>
                              <div className="claw-demo__stat-label">
                                总出勤人次
                              </div>
                            </div>
                            <div className="claw-demo__stat-item">
                              <div className="claw-demo__stat-value">12</div>
                              <div className="claw-demo__stat-label">
                                迟到次数
                              </div>
                            </div>
                            <div className="claw-demo__stat-item">
                              <div className="claw-demo__stat-value">3</div>
                              <div className="claw-demo__stat-label">
                                未打卡
                              </div>
                            </div>
                          </div>
                          <div className="claw-demo__chart">
                            <div className="claw-demo__chart-bars">
                              <div
                                className="claw-demo__chart-bar"
                                style={{ height: '45%' }}
                              >
                                <span>周一</span>
                              </div>
                              <div
                                className="claw-demo__chart-bar"
                                style={{ height: '60%' }}
                              >
                                <span>周二</span>
                              </div>
                              <div
                                className="claw-demo__chart-bar"
                                style={{ height: '40%' }}
                              >
                                <span>周三</span>
                              </div>
                              <div
                                className="claw-demo__chart-bar"
                                style={{ height: '75%' }}
                              >
                                <span>周四</span>
                              </div>
                              <div
                                className="claw-demo__chart-bar"
                                style={{ height: '55%' }}
                              >
                                <span>周五</span>
                              </div>
                            </div>
                          </div>
                          <p className="claw-demo__insight">
                            <BulbOutlined />
                            发现：周四出勤率最高（95%），周三有 3 人连续未打卡
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Message 4 - User */}
                    {visibleMessages >= 4 && (
                      <div className="claw-demo__message claw-demo__message--user claw-demo__message--fade-in">
                        <div className="claw-demo__bubble claw-demo__bubble--user">
                          给未打卡的 3 人发送提醒，并生成异常考勤报告
                        </div>
                      </div>
                    )}

                    {/* Message 5 - Bot with Actions */}
                    {visibleMessages >= 5 && (
                      <div className="claw-demo__message claw-demo__message--bot claw-demo__message--fade-in">
                        <div className="claw-demo__avatar">
                          <img src={clawIcon} alt="Claw" />
                        </div>
                        <div className="claw-demo__bubble claw-demo__bubble--bot">
                          <p>已完成以下操作：</p>
                          <div className="claw-demo__task-list">
                            <div className="claw-demo__task-item claw-demo__task-item--done">
                              <CheckCircleOutlined />
                              <span>已向 3 人发送打卡提醒（短信 + 钉钉）</span>
                            </div>
                            <div className="claw-demo__task-item claw-demo__task-item--done">
                              <CheckCircleOutlined />
                              <span>异常考勤报告已生成并发送至管理员</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Message 6 - User - 安全隐患识别 */}
                    {visibleMessages >= 6 && (
                      <div className="claw-demo__message claw-demo__message--user claw-demo__message--fade-in">
                        <div className="claw-demo__bubble claw-demo__bubble--user">
                          帮我对团队中的照片进行安全隐患识别
                        </div>
                      </div>
                    )}

                    {/* Message 7 - Bot - 安全隐患识别结果 */}
                    {visibleMessages >= 7 && (
                      <div className="claw-demo__message claw-demo__message--bot claw-demo__message--fade-in">
                        <div className="claw-demo__avatar">
                          <img src={clawIcon} alt="Claw" />
                        </div>
                        <div className="claw-demo__bubble claw-demo__bubble--bot">
                          <p>
                            <CheckCircleOutlined style={{ color: '#10b981' }} />
                            已完成团队照片安全隐患识别
                          </p>
                          <div className="claw-demo__report-summary">
                            <div className="claw-demo__report-item">
                              <FileTextOutlined />
                              <span>已生成安全隐患识别报告并发送到管理员</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Message 8 - User */}
                    {visibleMessages >= 8 && (
                      <div className="claw-demo__message claw-demo__message--user claw-demo__message--fade-in">
                        <div className="claw-demo__bubble claw-demo__bubble--user">
                          查看本月门店拜访情况，生成拜访报告
                        </div>
                      </div>
                    )}

                    {/* Message 9 - Bot with Summary */}
                    {visibleMessages >= 9 && (
                      <div className="claw-demo__message claw-demo__message--bot claw-demo__message--fade-in">
                        <div className="claw-demo__avatar">
                          <img src={clawIcon} alt="Claw" />
                        </div>
                        <div className="claw-demo__bubble claw-demo__bubble--bot">
                          <p>门店拜访情况分析：</p>
                          <div className="claw-demo__comparison-box">
                            <div className="claw-demo__comparison-item">
                              <span className="claw-demo__comparison-label">
                                拜访完成率
                              </span>
                              <span className="claw-demo__comparison-value claw-demo__comparison-value--up">
                                96.2% <ArrowUpOutlined /> +5.1%
                              </span>
                            </div>
                            <div className="claw-demo__comparison-item">
                              <span className="claw-demo__comparison-label">
                                未拜访门店
                              </span>
                              <span className="claw-demo__comparison-value claw-demo__comparison-value--down">
                                3 家 <ArrowDownOutlined /> -2 家
                              </span>
                            </div>
                          </div>
                          <p>
                            门店拜访报告已生成，包含拜访明细、未完成门店清单及改进建议。已发送至区域负责人邮箱。
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Message 10 - User */}
                    {/* {visibleMessages >= 10 && (
                      <div className="claw-demo__message claw-demo__message--user claw-demo__message--fade-in">
                        <div className="claw-demo__bubble claw-demo__bubble--user">
                          完美！明天早上 8 点提醒我查看考勤情况
                        </div>
                      </div>
                    )} */}

                    {/* Message 11 - Bot Final */}
                    {/* {visibleMessages >= 11 && (
                      <div className="claw-demo__message claw-demo__message--bot claw-demo__message--fade-in">
                        <div className="claw-demo__avatar">
                          <img src={clawIcon} alt="Claw" />
                        </div>
                        <div className="claw-demo__bubble claw-demo__bubble--bot">
                          <p>
                            <Icon type="clock-circle" />
                            已设置提醒：明天 08:00 查看考勤情况
                          </p>
                          <p className="claw-demo__summary">
                            今日任务完成！已处理考勤数据、发送提醒、生成报告并同步系统。你可以专注其他工作了
                            🦞
                          </p>
                        </div>
                      </div>
                    )} */}

                    {/* Typing Indicator */}
                    {isTyping && (
                      <div className="claw-demo__message claw-demo__message--bot claw-demo__message--fade-in">
                        <div className="claw-demo__avatar">
                          <img src={clawIcon} alt="Claw" />
                        </div>
                        <div className="claw-demo__bubble claw-demo__bubble--bot">
                          <div className="claw-demo__typing">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 用户实时发送的消息 */}
                    {userMessages.map((msg, idx) =>
                      msg.type === 'user' ? (
                        <div
                          key={`user-msg-${idx}`}
                          className="claw-demo__message claw-demo__message--user claw-demo__message--fade-in"
                        >
                          <div className="claw-demo__bubble claw-demo__bubble--user">
                            {msg.text}
                          </div>
                        </div>
                      ) : (
                        <div
                          key={`bot-msg-${idx}`}
                          className="claw-demo__message claw-demo__message--bot claw-demo__message--fade-in"
                        >
                          <div className="claw-demo__avatar">
                            <img src={clawIcon} alt="Claw" />
                          </div>
                          <div className="claw-demo__bubble claw-demo__bubble--bot">
                            {msg.text}
                          </div>
                        </div>
                      )
                    )}

                    {/* 机器人正在输入 */}
                    {isBotReplying && (
                      <div className="claw-demo__message claw-demo__message--bot claw-demo__message--fade-in">
                        <div className="claw-demo__avatar">
                          <img src={clawIcon} alt="Claw" />
                        </div>
                        <div className="claw-demo__bubble claw-demo__bubble--bot">
                          <div className="claw-demo__typing">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Input Area */}
                  <div className="claw-demo__input">
                    <div className="claw-demo__input-box">
                      <input
                        ref={inputRef}
                        className="claw-demo__input-field"
                        type="text"
                        placeholder={
                          activated
                            ? '输入消息，按回车进入 Claw 工作台...'
                            : '给你的 今日龙虾Claw 发消息...'
                        }
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSendMessage()
                        }}
                      />
                      <div
                        className={`claw-demo__send ${
                          inputValue.trim() ? 'claw-demo__send--active' : ''
                        }`}
                        onClick={handleSendMessage}
                      >
                        <ArrowRightOutlined />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {isKaChannel ? (
          <section className="claw-pricing" ref={pricingRef}>
            <div className="claw-pricing__container">
              <h2 className="claw-pricing__title">选择适合你的套餐</h2>
              <p className="claw-pricing__subtitle">
                每个套餐都是独立专属实例，内测期间全功能免费体验
              </p>
              <div className="claw-pricing__grid">
                {PLANS.map((plan) => (
                  <div
                    key={plan.key}
                    className={`claw-plan${
                      plan.highlighted ? ' claw-plan--featured' : ''
                    }${plan.dark ? ' claw-plan--premium' : ''}`}
                  >
                    {plan.badgeText && (
                      <div
                        className={`claw-plan__badge${
                          plan.dark ? ' claw-plan__badge--gold' : ''
                        }`}
                      >
                        {plan.badgeText}
                      </div>
                    )}
                    <h3 className="claw-plan__name">{plan.name}</h3>
                    {plan.tagline && (
                      <p className="claw-plan__tagline">{plan.tagline}</p>
                    )}
                    <div className="claw-plan__price">
                      <span className="claw-plan__currency">¥</span>
                      <span className="claw-plan__amount">{plan.price}</span>
                      <span className="claw-plan__price-label">/年</span>
                    </div>
                    <ul className="claw-plan__features">
                      {plan.features.map((f) => (
                        <li
                          key={f.text}
                          className={
                            f.highlight ? 'claw-plan__feature--highlight' : ''
                          }
                        >
                          <CheckOutlined className="claw-plan__check" />
                          {f.text}
                        </li>
                      ))}
                    </ul>
                    {plan.tags && (
                      <div className="claw-plan__tags">
                        {plan.tags.map((tag) => (
                          <span key={tag.label} className="claw-plan__tag">
                            {tag.icon && <ClawIcon name={tag.icon} />}
                            {tag.label}
                          </span>
                        ))}
                      </div>
                    )}
                    <Button
                      size="large"
                      block
                      className="claw-plan__button"
                      onClick={() => {
                        trackClawPage(
                          plan.key === 'standard' ? 'buyMedium' : 'buyLarge'
                        )
                        setSelectedPlan(plan)
                        setPaymentModalVisible(true)
                      }}
                    >
                      {plan.buttonText}
                    </Button>
                  </div>
                ))}
              </div>

              {/* Contact Strip - KA */}
              <div className="claw-custom__contact" style={{ marginTop: 48 }}>
                <div className="claw-custom__contact-accent" />
                <div className="claw-custom__contact-body">
                  <div className="claw-custom__contact-left">
                    <h3 className="claw-custom__contact-title">
                      立即咨询，获取专属定制方案
                    </h3>
                    <p className="claw-custom__contact-desc">
                      扫码添加微信，我们将在48小时内提供专属方案
                    </p>
                  </div>
                  <div className="claw-custom__contact-qr">
                    <img src={wanganqiangQrCode} alt="二维码" />
                  </div>
                  <div className="claw-custom__contact-right">
                    <span className="claw-custom__contact-name">
                      大客户经理 王安强
                    </span>
                    <div className="claw-custom__contact-tags">
                      {[
                        '专属技能定制',
                        '1v1深度服务',
                        '优先排期交付',
                        '免费需求评估'
                      ].map((tag) => (
                        <span key={tag} className="claw-custom__contact-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="claw-custom__contact-trust">
                      <StarFilled />
                      <span>已服务500+企业客户</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="claw-custom" ref={pricingRef}>
            <div className="claw-custom__container">
              {/* Banner */}
              <div className="claw-custom__banner">
                <FireOutlined />
                <span>当前活动火爆，仅支持需求定制服务</span>
              </div>

              {/* Benefits Card */}
              <div className="claw-custom__card">
                <div className="claw-custom__card-header">
                  <div className="claw-custom__card-badge">
                    <CrownOutlined />
                  </div>
                  <h3 className="claw-custom__card-title">专属定制服务</h3>
                </div>
                <p className="claw-custom__card-subtitle">
                  为您的业务量身打造数字员工解决方案
                </p>
                <div className="claw-custom__benefits-grid">
                  {[
                    {
                      icon: 'tool',
                      title: '专属技能定制',
                      desc: 'AI技能按需开发，完美匹配业务流程',
                      color: 'blue'
                    },
                    {
                      icon: 'customer-service',
                      title: '1v1深度服务',
                      desc: '专属产品顾问全程跟进，快速响应',
                      color: 'green'
                    },
                    {
                      icon: 'rocket',
                      title: '优先排期交付',
                      desc: '定制需求优先处理，极速上线',
                      color: 'purple'
                    },
                    {
                      icon: 'safety-certificate',
                      title: '数据安全保障',
                      desc: '私有化部署，企业级数据安全防护',
                      color: 'amber'
                    }
                  ].map((item) => (
                    <div key={item.title} className="claw-custom__benefit-item">
                      <div
                        className={`claw-custom__benefit-icon claw-custom__benefit-icon--${item.color}`}
                      >
                        <ClawIcon name={item.icon} />
                      </div>
                      <div className="claw-custom__benefit-text">
                        <span className="claw-custom__benefit-title">
                          {item.title}
                        </span>
                        <span className="claw-custom__benefit-desc">
                          {item.desc}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trust Stats */}
              <div className="claw-custom__stats">
                {[
                  { num: '500+', label: '已服务企业', color: 'blue' },
                  { num: '98%', label: '客户满意度', color: 'green' },
                  { num: '48h', label: '需求响应时间', color: 'purple' }
                ].map((stat) => (
                  <div key={stat.label} className="claw-custom__stat-card">
                    <span className="claw-custom__stat-num">{stat.num}</span>
                    <span className="claw-custom__stat-label">
                      {stat.label}
                    </span>
                    <span
                      className={`claw-custom__stat-line claw-custom__stat-line--${stat.color}`}
                    />
                  </div>
                ))}
              </div>

              {/* Service Flow */}
              <div className="claw-custom__flow">
                <h4 className="claw-custom__flow-title">服务流程</h4>
                <div className="claw-custom__flow-steps">
                  {[
                    {
                      num: '1',
                      title: '咨询需求',
                      desc: '扫码联系经理',
                      color: 'blue'
                    },
                    {
                      num: '2',
                      title: '免费评估',
                      desc: '业务需求分析',
                      color: 'green'
                    },
                    {
                      num: '3',
                      title: '定制开发',
                      desc: '专属方案开发',
                      color: 'purple'
                    },
                    {
                      num: '4',
                      title: '交付上线',
                      desc: '部署+培训支持',
                      color: 'amber'
                    }
                  ].map((step, idx) => (
                    <React.Fragment key={step.num}>
                      {idx > 0 && (
                        <RightOutlined className="claw-custom__flow-arrow" />
                      )}
                      <div className="claw-custom__flow-step">
                        <div
                          className={`claw-custom__flow-circle claw-custom__flow-circle--${step.color}`}
                        >
                          {step.num}
                        </div>
                        <span className="claw-custom__flow-step-title">
                          {step.title}
                        </span>
                        <span className="claw-custom__flow-step-desc">
                          {step.desc}
                        </span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Contact Strip */}
              <div className="claw-custom__contact">
                <div className="claw-custom__contact-accent" />
                <div className="claw-custom__contact-body">
                  <div className="claw-custom__contact-left">
                    <h3 className="claw-custom__contact-title">
                      立即咨询，获取专属定制方案
                    </h3>
                    <p className="claw-custom__contact-desc">
                      扫码添加微信，我们将在48小时内提供专属方案
                    </p>
                  </div>
                  <div className="claw-custom__contact-qr">
                    <img src={wanganqiangQrCode} alt="二维码" />
                  </div>
                  <div className="claw-custom__contact-right">
                    <span className="claw-custom__contact-name">
                      大客户经理 王安强
                    </span>
                    <div className="claw-custom__contact-tags">
                      {[
                        '专属技能定制',
                        '1v1深度服务',
                        '优先排期交付',
                        '免费需求评估'
                      ].map((tag) => (
                        <span key={tag} className="claw-custom__contact-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="claw-custom__contact-trust">
                      <StarFilled />
                      <span>已服务500+企业客户</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="claw-custom__cta">
                <div className="claw-custom__cta-badge">
                  <ClockCircleOutlined />
                  <span>本月定制名额仅剩 3 个</span>
                </div>
                <Button
                  size="large"
                  block
                  className="claw-custom__cta-button"
                  onClick={() => {
                    trackClawPage('contactUs')
                    setShowServiceQr(true)
                  }}
                >
                  立即咨询定制方案 →
                </Button>
                <p className="claw-custom__cta-sub">
                  免费需求评估，48小时内专属方案
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Countdown Section */}
        {/* <section className="claw-countdown">
          <div className="claw-countdown__container">
            <div className="claw-countdown__title">
              <Icon type="clock-circle" />
              <span>限时优惠 即将截止</span>
            </div>
            <p className="claw-countdown__subtitle">
              活动截止时间：2026年4月6日 23:59:59
            </p>
            <div className="claw-countdown__timer">
              {[
                { value: countdown.days, label: '天' },
                { value: countdown.hours, label: '时' },
                { value: countdown.minutes, label: '分' },
                { value: countdown.seconds, label: '秒' }
              ].map((item, idx) => (
                <React.Fragment key={item.label}>
                  {idx > 0 && <span className="claw-countdown__sep">:</span>}
                  <div className="claw-countdown__box">
                    <span className="claw-countdown__num">
                      {String(item.value).padStart(2, '0')}
                    </span>
                    <span className="claw-countdown__label">{item.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div className="claw-countdown__urgency">
              <span className="claw-countdown__dot" />
              <span>名额有限，活动即将结束</span>
            </div>
          </div>
        </section> */}

        {/* Features Section */}
        {/* <section className="claw-features">
          <div className="claw-features__container">
            <h2 className="claw-features__title">3 步开始使用</h2>
            <div className="claw-features__grid">
              {HOW_TO_START.map((item) => (
                <div key={item.step} className="claw-feature-card">
                  <div className="claw-feature-card__icon">
                    <Icon type={item.icon as any} />
                  </div>
                  <h3 className="claw-feature-card__title">{item.title}</h3>
                  <p className="claw-feature-card__desc">{item.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </section> */}

        {/* Comparison Section */}
        <section className="claw-comparison">
          <div className="claw-comparison__container">
            <h2 className="claw-comparison__title">
              为什么选择今日龙虾 Claw？
            </h2>
            <div className="claw-comparison__table">
              <div className="claw-comparison__header">
                <div className="claw-comparison__col"></div>
                <div className="claw-comparison__col claw-comparison__col--normal">
                  普通方案
                </div>
                <div className="claw-comparison__col claw-comparison__col--claw">
                  <ThunderboltOutlined />
                  今日龙虾 Claw
                </div>
              </div>
              {COMPARISON.map((item, idx) => (
                <div
                  key={idx}
                  className={`claw-comparison__row ${
                    item.highlight ? 'claw-comparison__row--highlight' : ''
                  }`}
                >
                  <div className="claw-comparison__feature">{item.feature}</div>
                  <div className="claw-comparison__value claw-comparison__value--normal">
                    <CloseOutlined />
                    {item.normal}
                  </div>
                  <div className="claw-comparison__value claw-comparison__value--claw">
                    <CheckOutlined />
                    {item.claw}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        {/* <section className="claw-benefits">
          <div className="claw-benefits__container">
            <h2 className="claw-benefits__title">今日龙虾 Claw 的核心优势</h2>
            <div className="claw-benefits__grid">
              {BENEFITS.map((item) => (
                <div key={item.title} className="claw-benefit-card">
                  <div className="claw-benefit-card__icon">
                    <Icon type={item.icon as any} />
                  </div>
                  <h3 className="claw-benefit-card__title">{item.title}</h3>
                  <p className="claw-benefit-card__desc">{item.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </section> */}
      </main>

      {/* 支付弹窗 */}
      {/* 悬浮联系客服 */}
      {/* <div className="claw-contact-fab">
        {showServiceQr && (
          <>
            <div
              className="claw-contact-fab__overlay"
              onClick={() => setShowServiceQr(false)}
            />
            <div className="claw-contact-fab__qr">
              <img src={hejingQrCode} alt="客服二维码" />
              <span>扫码添加专属客服</span>
            </div>
          </>
        )}
        <div
          className="claw-contact-fab__btn"
          onClick={() => {
            if (!showServiceQr) trackClawPage('contactUs')
            setShowServiceQr(!showServiceQr)
          }}
        >
          <Icon type="customer-service" />
          <span>联系客服</span>
        </div>
      </div> */}

      <ClawPaymentModal
        visible={paymentModalVisible}
        plan={selectedPlan}
        onClose={() => {
          setPaymentModalVisible(false)
          setSelectedPlan(null)
        }}
        onTrack={trackClawPage}
      />
    </div>
  )
}

export default TodayClaw
